import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { buildVerifyPath } from '../../quiz/pendingQuiz';
import { MediaService } from '../../services/mediaService';
import styles from './OnboardingVerify.module.css';
import { ONBOARDING_ACTIONS, ONBOARDING_SKIP_REASONS, ONBOARDING_STEPS } from '@aluvo/constants/src/onboardingDiagnostics';
import { coinEligibleTitles, titleKey } from '@aluvo/constants/src/onboardingTitles';
import { OnboardingDiagnostics } from '../../diagnostics/onboardingDiagnostics';

const VERIFY = ONBOARDING_STEPS.VERIFY.key;

/**
 * Where a user goes when they leave onboarding without starting a challenge:
 * back to what a gate signup was doing, otherwise Discover.
 */
export function onboardingExitPath() {
    try {
        const raw = sessionStorage.getItem('aluvo_pending_intent');
        const intent = raw ? JSON.parse(raw) : null;
        if (intent?.returnUrl && (!intent.expiresAt || intent.expiresAt > Date.now())) return intent.returnUrl;
    } catch { /* ignore */ }
    return '/discover';
}

// "You're all set!" — choose one of your picks to earn its coin (the first
// challenge) or explore. Nothing is awarded here. Reached without picks (an
// old link / reload), it bypasses itself instead of showing an empty list.
export default function OnboardingVerify() {
    const { state }  = useLocation();
    const navigate   = useNavigate();
    const [watchedTitles] = useState(() => coinEligibleTitles(state?.watchedTitles));

    // loadingQuiz: titleKey of whichever title is currently loading
    const [loadingQuiz, setLoadingQuiz] = useState(null);

    // Season picker state for TV shows
    const [seasonPicker, setSeasonPicker] = useState(null); // { title, tmdbId, seasons }
    const [loadingSeasons, setLoadingSeasons] = useState(false);
    const viewedRef = useRef(false);

    // Quiz handoff: completed = a quiz was requested (recorded by the server).
    useEffect(() => {
        if (viewedRef.current) return;
        viewedRef.current = true;
        if (!watchedTitles.length) {
            const picked = Array.isArray(state?.watchedTitles) ? state.watchedTitles.length : 0;
            // Only report a bypass when onboarding sent the user here; a bare
            // visit to /onboarding/verify is not an onboarding event.
            if (state) {
                OnboardingDiagnostics.stepAutoSkipped(VERIFY, picked ? ONBOARDING_SKIP_REASONS.NO_ELIGIBLE_MOVIES : ONBOARDING_SKIP_REASONS.NO_SELECTED_MOVIES, { selected_count: picked });
            }
            navigate(onboardingExitPath(), { replace: true });
            return;
        }
        OnboardingDiagnostics.stepViewed(VERIFY, { selected_count: watchedTitles.length });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!watchedTitles.length) return null;

    async function handleEarnCoin(item) {
        if (loadingQuiz) return;
        OnboardingDiagnostics.stepAction(VERIFY, ONBOARDING_ACTIONS.CONTINUE, { media_type: item.kind });

        if (item.kind === 'tv') {
            setLoadingQuiz(titleKey(item.kind, item.tmdbId));
            setLoadingSeasons(true);
            const startedAt = Date.now();
            try {
                const data = await MediaService.getTvSeasons(item.tmdbId);
                const available = (data ?? []).filter(s => s.canVerify);
                if (!available.length) {
                    alert('No seasons are ready for a challenge yet.');
                    return;
                }
                setSeasonPicker({ title: item.title, tmdbId: item.tmdbId, seasons: available });
            } catch (error) {
                OnboardingDiagnostics.stepError(VERIFY, {
                    operation: 'load_seasons', endpoint: 'tv_seasons', error, blocking: true, durationMs: Date.now() - startedAt, extra: { media_type: 'tv' },
                });
                alert('Could not load seasons. Please try again.');
            } finally {
                setLoadingSeasons(false);
                setLoadingQuiz(null);
            }
        } else {
            await launchQuiz(item.tmdbId, 'movie', null, item.title);
        }
    }

    // The /verify page owns quiz creation (one attempt per tap) plus the DOB and
    // email-verification gates; onboarding only points the user at it.
    function launchQuiz(tmdbId, kind, seasonNumber, mediaTitle) {
        const path = buildVerifyPath(kind, Number(tmdbId), seasonNumber ?? null);
        if (!path) return;
        setLoadingQuiz(titleKey(kind, tmdbId));
        setSeasonPicker(null);
        navigate(path, { state: { sourceScreen: 'onboarding', mediaTitle } });
    }


    return (
        <div className={styles.page}>
            <div className={styles.inner}>
                <span className={styles.icon}>🎉</span>
                <h1 className={styles.headline}>You're all set!</h1>
                <p className={styles.subtext}>
                    Start verifying what you've watched and earn your first coins below.
                </p>

                <div className={styles.titleList}>
                    {watchedTitles.map(item => {
                        const isLoading = loadingQuiz === titleKey(item.kind, item.tmdbId);
                        return (
                            <div key={titleKey(item.kind, item.tmdbId)} className={styles.titleRow}>
                                {item.posterUrl ? (
                                    <img src={item.posterUrl} alt={item.title} className={styles.poster} />
                                ) : (
                                    <div className={styles.posterPlaceholder}>
                                        {item.kind === 'tv' ? '📺' : '🎬'}
                                    </div>
                                )}
                                <div className={styles.titleInfo}>
                                    <p className={styles.titleName}>{item.title}</p>
                                    <p className={styles.titleKind}>
                                        {item.kind === 'tv' ? 'TV Series' : 'Movie'}
                                    </p>
                                </div>
                                <button
                                    className={styles.earnBtn}
                                    onClick={() => handleEarnCoin(item)}
                                    disabled={!!loadingQuiz}
                                >
                                    {isLoading ? 'Loading…' : '🪙 Earn your coin'}
                                </button>
                            </div>
                        );
                    })}
                </div>

                {/* A guest who signed up through an auth gate resumes what they were doing. */}
                <Link to={onboardingExitPath()} className={styles.discoverLink} onClick={() => OnboardingDiagnostics.stepAction(VERIFY, ONBOARDING_ACTIONS.EXPLORE, { reason: ONBOARDING_SKIP_REASONS.USER_SKIPPED })}>
                    Explore more titles →
                </Link>
            </div>

            {/* Season picker modal for TV shows */}
            {seasonPicker && (
                <div className={styles.modalOverlay} onClick={() => setSeasonPicker(null)}>
                    <div className={styles.modalSheet} onClick={e => e.stopPropagation()}>
                        <h2 className={styles.modalTitle}>{seasonPicker.title}</h2>
                        <p className={styles.modalSubtitle}>Pick a season to verify</p>
                        {seasonPicker.seasons.map(season => (
                            <button
                                key={season.seasonNumber}
                                className={styles.seasonRow}
                                onClick={() => launchQuiz(
                                    seasonPicker.tmdbId,
                                    'tv',
                                    season.seasonNumber,
                                    `${seasonPicker.title} — Season ${season.seasonNumber}`
                                )}
                            >
                                {season.posterUrl && (
                                    <img src={season.posterUrl} alt={season.name} className={styles.seasonPoster} />
                                )}
                                <span className={styles.seasonName}>
                                    {season.name ?? `Season ${season.seasonNumber}`}
                                </span>
                                <span className={styles.seasonArrow}>›</span>
                            </button>
                        ))}
                        <button className={styles.cancelBtn} onClick={() => setSeasonPicker(null)}>
                            Cancel
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}